import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import '../styles/Login.css';

const Login = () => {
  const [username, setUsername] = useState('admin@example.com');
  const [password, setPassword] = useState('changethis');
  const [error, setError] = useState('');
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    const details = {
      grant_type: 'password',
      username: username,
      password: password,
      scope: '',
      client_id: '',
      client_secret: ''
    };

    const formBody = Object.keys(details)
      .map(key => encodeURIComponent(key) + '=' + encodeURIComponent(details[key]))
      .join('&');

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/login/access-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
          'accept': 'application/json'
        },
        body: formBody
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Login failed');
      }

      const data = await response.json();
      localStorage.setItem('accessToken', data.access_token);

      const userResponse = await fetch(`${process.env.REACT_APP_API_URL}/users/me`, {
        headers: {
          'Authorization': `Bearer ${data.access_token}`,
          'accept': 'application/json',
        }
      });
      if (userResponse.ok) {
        const userData = await userResponse.json();
        localStorage.setItem('userFullName', userData.full_name || userData.email || '');
        localStorage.setItem('hasCompletedPlacement', userData.has_completed_placement ? 'true' : 'false');
        localStorage.setItem('userAvatar', userData.avatar || '');
        navigate(userData.has_completed_placement ? '/dashboard' : '/onboarding');
        return;
      }

      navigate('/dashboard');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-stretch overflow-hidden bg-hp-surface font-body text-hp-on-surface">

      {/* ── LEFT HERO PANEL (60%) ── */}
      <section className="hidden lg:flex lg:w-3/5 hp-hero-gradient relative flex-col justify-between p-12 overflow-hidden">
        {/* Logo */}
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <div className="bg-white/90 backdrop-blur-md p-3 rounded-xl shadow-sm">
              <span className="material-symbols-outlined text-hupath-scout text-3xl" style={{ fontVariationSettings: "'FILL' 1" }}>park</span>
            </div>
            <h1 className="font-headline font-black text-3xl text-hupath-scout tracking-tight">HuPath</h1>
          </div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-2xl mb-12">
          <div className="inline-flex items-center gap-2 bg-white/30 backdrop-blur-xl px-4 py-2 rounded-full mb-6 text-hupath-forest font-headline font-bold text-sm">
            <span className="material-symbols-outlined text-sm">auto_awesome</span>
            Explore the Path, Discover the Future
          </div>
          <h2 className="font-headline text-5xl xl:text-6xl font-extrabold text-hupath-ink leading-[1.1] mb-6">
            Chinh phục Cambridge English cùng HuPath! 🦉
          </h2>
          <p className="font-body text-xl text-hupath-ink/80 leading-relaxed max-w-lg italic">
            Biến việc học tiếng Anh thành một cuộc phiêu lưu khám phá rừng xanh tri thức. Sẵn sàng lên đường chưa nào?
          </p>
        </div>

        {/* Decorative Elements */}
        <div className="absolute inset-0 pointer-events-none">
          {/* Main Illustration Image */}
          <img
            alt="Kids learning English"
            className="absolute bottom-[-10%] right-[-5%] w-[80%] h-auto opacity-90 mix-blend-multiply"
            src="https://lh3.googleusercontent.com/aida-public/AB6AXuDVBmRDXhSrlkrPckzvUAULqpzv4S88_rQKUN1dCD3Go7kFQPghz6Qc_7MmTaXUGnXivkCZJrt8c84d3YedVtRFGiFm0ZA1Lfwb2y8GFqTbd5TDyRtGLUcktEHdw9hZV9G6GJjSVIj5k9jzhZw_c7PTZRNMkiVoveVci-YBI5yB5D98-NVxzqEwRM_3pHsSOVTtqQ7QFlARj1QJQCuWfTCWWs39WlgWiakuy8uO6SA5R2_Nrk6jjNodJ2O3NzOoc91kHgbEkSnTFg"
          />
          {/* Floating Cloud Ornaments */}
          <div className="absolute top-1/4 right-1/4 w-48 h-24 bg-white/40 blur-2xl rounded-full opacity-60"></div>
          <div className="absolute bottom-1/3 left-1/4 w-64 h-32 bg-hupath-gold/20 blur-3xl rounded-full opacity-50"></div>
          <div className="absolute top-20 right-1/3 text-white/40">
            <span className="material-symbols-outlined text-4xl">forest</span>
          </div>
          <div className="absolute bottom-1/4 left-10 text-hupath-gold/40">
            <span className="material-symbols-outlined text-5xl">star</span>
          </div>
        </div>
      </section>

      {/* ── RIGHT FORM PANEL (40%) ── */}
      <main className="w-full lg:w-2/5 bg-white flex items-center justify-center p-8 md:p-16 relative">
        <div className="absolute top-0 right-0 w-64 h-64 bg-hp-surface-container-low rounded-full -translate-y-1/2 translate-x-1/2 -z-0"></div>

        <div className="w-full max-w-md relative z-10">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-2 mb-10">
            <span className="material-symbols-outlined text-hupath-scout text-2xl" style={{ fontVariationSettings: "'FILL' 1" }}>park</span>
            <span className="font-headline font-bold text-xl text-hupath-scout">HuPath</span>
          </div>

          {/* Header */}
          <div className="mb-10">
            <h3 className="font-headline text-3xl font-bold text-hp-on-surface mb-3">Đăng nhập</h3>
            <p className="font-body text-lg text-hp-on-surface-variant">
              Chào mừng trở lại! Hãy tiếp tục hành trình học tập nhé!
            </p>
          </div>

          {/* Login Form */}
          <form className="space-y-6" onSubmit={handleSubmit}>
            {/* Email */}
            <div className="space-y-2">
              <label className="font-headline font-semibold text-sm text-hp-on-surface-variant ml-1" htmlFor="username">Email</label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-hp-outline">
                  <span className="material-symbols-outlined">mail</span>
                </div>
                <input
                  id="username" type="email" placeholder="Email của bạn"
                  value={username} onChange={(e) => setUsername(e.target.value)} required
                  className="block w-full pl-12 pr-4 py-4 bg-hp-surface-container-low border-none rounded-xl font-body text-hp-on-surface placeholder:text-hp-outline focus:ring-2 focus:ring-hupath-scout/30 transition-all"
                />
              </div>
            </div>

            {/* Password */}
            <div className="space-y-2">
              <div className="flex justify-between items-center px-1">
                <label className="font-headline font-semibold text-sm text-hp-on-surface-variant" htmlFor="password">Mật khẩu</label>
                <a className="font-headline font-bold text-xs text-hupath-scout hover:underline" href="/#">Quên mật khẩu?</a>
              </div>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-hp-outline">
                  <span className="material-symbols-outlined">lock</span>
                </div>
                <input
                  id="password" type={isPasswordVisible ? 'text' : 'password'} placeholder="Mật khẩu"
                  value={password} onChange={(e) => setPassword(e.target.value)} required
                  className="block w-full pl-12 pr-12 py-4 bg-hp-surface-container-low border-none rounded-xl font-body text-hp-on-surface placeholder:text-hp-outline focus:ring-2 focus:ring-hupath-scout/30 transition-all"
                />
                <button type="button" onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-hp-outline hover:text-hupath-scout transition-colors"
                  aria-label="Toggle password visibility">
                  <span className="material-symbols-outlined">{isPasswordVisible ? 'visibility' : 'visibility_off'}</span>
                </button>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-300 rounded-xl px-4 py-3 text-red-600 text-sm font-medium">
                <span className="material-symbols-outlined text-lg">error</span>
                {error}
              </div>
            )}

            {/* Primary CTA */}
            <button type="submit" disabled={isLoading}
              className="w-full hp-button-gradient text-white font-headline font-bold py-4 rounded-xl shadow-[0px_8px_24px_rgba(42,107,48,0.25)] hover:scale-[1.02] active:scale-95 transition-all flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100">
              {isLoading ? (
                <><span className="material-symbols-outlined lf-spin text-xl">progress_activity</span> Đang xử lý...</>
              ) : (
                <>Đăng nhập <span className="material-symbols-outlined">hiking</span></>
              )}
            </button>

            {/* Divider */}
            <div className="relative flex items-center py-4">
              <div className="flex-grow border-t border-hp-outline-variant/30"></div>
              <span className="flex-shrink mx-4 font-headline text-xs font-medium text-hp-outline uppercase tracking-widest">hoặc</span>
              <div className="flex-grow border-t border-hp-outline-variant/30"></div>
            </div>

            {/* Google Login */}
            <button type="button"
              className="w-full bg-white border border-hp-outline-variant/30 py-4 rounded-xl font-headline font-semibold text-hp-on-surface flex items-center justify-center gap-3 hover:bg-hp-surface-container-low transition-colors shadow-sm">
              <svg className="w-5 h-5" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
              Tiếp tục với Google
            </button>
          </form>

          {/* Bottom Link */}
          <div className="mt-12 text-center">
            <p className="font-body text-hp-on-surface-variant">
              Chưa có tài khoản?{' '}
              <Link to="/register" className="font-headline font-bold text-hupath-scout hover:underline ml-1">Đăng ký ngay</Link>
            </p>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-4 text-hp-outline-variant/50">
          <span className="material-symbols-outlined text-3xl">forest</span>
          <span className="material-symbols-outlined text-3xl" style={{ fontVariationSettings: "'FILL' 1" }}>star</span>
          <span className="material-symbols-outlined text-3xl">forest</span>
        </div>
      </main>
    </div>
  );
};

export default Login;
